import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Tag, Check, Calendar } from 'lucide-react';

interface ServicePromotion {
  is_active: boolean;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  start_date?: string;
  end_date?: string;
}

interface PromotionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (promotion: ServicePromotion | null) => void;
  currentPromotion?: ServicePromotion;
  serviceName: string;
}

export function PromotionDialog({ isOpen, onClose, onSave, currentPromotion, serviceName }: PromotionDialogProps) {
  const [hasPromotion, setHasPromotion] = useState(!!currentPromotion?.is_active);
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>(
    currentPromotion?.discount_type || 'percentage'
  );
  const [discountValue, setDiscountValue] = useState(currentPromotion?.discount_value || 10);
  const [startDate, setStartDate] = useState(currentPromotion?.start_date || '');
  const [endDate, setEndDate] = useState(currentPromotion?.end_date || '');

  useEffect(() => {
    if (currentPromotion) {
      setHasPromotion(currentPromotion.is_active);
      setDiscountType(currentPromotion.discount_type);
      setDiscountValue(currentPromotion.discount_value);
      setStartDate(currentPromotion.start_date || '');
      setEndDate(currentPromotion.end_date || '');
    }
  }, [currentPromotion]);

  useEffect(() => {
    if (isOpen) {
      const event = new CustomEvent('ui:service-modal', { detail: { open: true } });
      window.dispatchEvent(event);
    } else {
      const event = new CustomEvent('ui:service-modal', { detail: { open: false } });
      window.dispatchEvent(event);
    }
  }, [isOpen]);

  const handleSave = () => {
    if (!hasPromotion) {
      onSave(null);
    } else {
      onSave({
        is_active: true,
        discount_type: discountType,
        discount_value: discountValue,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });
    }
    onClose();
  };

  const handleRemove = () => {
    onSave(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-end sm:items-center justify-center"
        style={{ zIndex: 9999 }}
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl flex flex-col overflow-hidden shadow-2xl"
          style={{ maxHeight: 'calc(100vh - 80px)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-5 text-white">
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={onClose}
                className="p-2 -mr-2 text-white/80 active:text-white rounded-lg active:bg-white/10"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2">
                <Tag className="h-6 w-6" />
                <h2 className="text-xl font-bold">מבצע</h2>
              </div>
              <div className="w-9" />
            </div>
            <p className="text-sm text-white/90 text-center">{serviceName}</p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Enable/Disable Promotion */}
            <div
              onClick={() => setHasPromotion(!hasPromotion)}
              className={`p-5 rounded-2xl border-2 transition-all cursor-pointer ${
                hasPromotion
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      hasPromotion ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <Tag className={`h-6 w-6 ${hasPromotion ? 'text-white' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-base">
                      {hasPromotion ? 'מבצע פעיל' : 'הפעל מבצע'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {hasPromotion ? 'המבצע פעיל כעת' : 'לחץ להפעלת מבצע'}
                    </p>
                  </div>
                </div>
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    hasPromotion ? 'border-green-500 bg-green-500' : 'border-gray-300'
                  }`}
                >
                  {hasPromotion && <Check className="h-4 w-4 text-white" />}
                </div>
              </div>
            </div>

            {/* Promotion Settings */}
            <AnimatePresence>
              {hasPromotion && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4"
                >
                  {/* Discount Type */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">סוג הנחה</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setDiscountType('percentage')}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          discountType === 'percentage'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="text-2xl font-bold mb-1">%</div>
                        <div className="text-sm font-medium">אחוזים</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setDiscountType('fixed')}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          discountType === 'fixed'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="text-2xl font-bold mb-1">₪</div>
                        <div className="text-sm font-medium">סכום קבוע</div>
                      </button>
                    </div>
                  </div>

                  {/* Discount Value */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">
                      גובה ההנחה
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={discountValue}
                        onChange={(e) => setDiscountValue(parseFloat(e.target.value))}
                        className="w-full p-4 pr-12 text-xl font-bold border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-right"
                        min="0"
                        step={discountType === 'percentage' ? '1' : '0.01'}
                      />
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-500">
                        {discountType === 'percentage' ? '%' : '₪'}
                      </span>
                    </div>
                    {discountValue > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl text-center"
                      >
                        <p className="text-green-700 font-semibold">
                          {discountType === 'percentage'
                            ? `הנחה של ${discountValue}%`
                            : `הנחה של ${discountValue}₪`}
                        </p>
                      </motion.div>
                    )}
                  </div>

                  {/* Optional: Date Range */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">
                      <Calendar className="inline h-4 w-4 ml-1" />
                      תוקף (אופציונלי)
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">מתאריך</label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">עד תאריך</label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="bg-white border-t border-gray-200 p-4 space-y-3">
            {currentPromotion?.is_active && (
              <button
                type="button"
                onClick={handleRemove}
                className="w-full py-3 px-4 border-2 border-red-500 text-red-600 rounded-xl font-semibold hover:bg-red-50 transition-all"
              >
                הסר מבצע
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={hasPromotion && (!discountValue || discountValue <= 0)}
              className="w-full py-3 px-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
            >
              <Check className="h-5 w-5" />
              שמור
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
